import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AccordionModule } from 'primeng/accordion';
import { CardModule } from 'primeng/card';

import { WritingHistoryService, type AiResult, type WritingExamRecord } from '../../../services/writing-history.service';
import { WritingSessionService } from '../../../services/writing-session.service';
import type { TopicCombination } from '../../../models';

const MIN_WORDS_FOR_GRADING = 30;
const TASK_WORD_LIMITS = [
  { min: 60, max: 120 },
  { min: 120, max: 150 },
  { min: 150, max: 180 },
] as const;

type OriginalSegment = { text: string; type: 'normal' | 'deleted' };
type CorrectedSegment = { text: string; type: 'normal' | 'inserted' };

export interface TaskResult {
  originalText: string;
  correctedText: string;
  nclcLevel: string;
  wordCount: number;
  wordLimit: { min: number; max: number };
  taskFulfillment: string;
  grammarErrors: Array<{ original: string; correction: string; explanation: string }>;
  vocabularyUpgrades: Array<{ original: string; suggestion: string; reason: string }>;
  connectorsUsed: string[];
  connectorsMissed: string[];
  originalSegments: OriginalSegment[];
  correctedSegments: CorrectedSegment[];
  highScoringPatternsFound: Array<{ excerpt: string; patternName: string; explanation: string }>;
  suggestedPatterns: Array<{ patternName: string; originalSentence: string; improvedSentence: string; explanation: string }>;
}

function computeDiffSegments(
  originalText: string,
  correctedText: string
): { originalSegments: OriginalSegment[]; correctedSegments: CorrectedSegment[] } {
  const originalTokens = originalText.trim() ? originalText.trim().split(/\s+/) : [];
  const correctedTokens = correctedText.trim() ? correctedText.trim().split(/\s+/) : [];

  const m = originalTokens.length;
  const n = correctedTokens.length;

  if (m === 0 && n === 0) {
    return {
      originalSegments: [{ text: '', type: 'normal' }],
      correctedSegments: [{ text: '', type: 'normal' }],
    };
  }

  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (originalTokens[i - 1] === correctedTokens[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const lcsIndices: Array<{ i: number; j: number }> = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (originalTokens[i - 1] === correctedTokens[j - 1]) {
      lcsIndices.unshift({ i: i - 1, j: j - 1 });
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  const originalSegments: OriginalSegment[] = [];
  const correctedSegments: CorrectedSegment[] = [];

  const pushOriginal = (type: OriginalSegment['type'], text: string) => {
    if (!text) return;
    const last = originalSegments[originalSegments.length - 1];
    if (last && last.type === type) {
      last.text += ' ' + text;
    } else {
      originalSegments.push({ type, text });
    }
  };

  const pushCorrected = (type: CorrectedSegment['type'], text: string) => {
    if (!text) return;
    const last = correctedSegments[correctedSegments.length - 1];
    if (last && last.type === type) {
      last.text += ' ' + text;
    } else {
      correctedSegments.push({ type, text });
    }
  };

  let oIndex = 0;
  let cIndex = 0;
  let k = 0;

  while (oIndex < m || cIndex < n) {
    const nextCommon = lcsIndices[k];

    while (oIndex < m && (!nextCommon || oIndex < nextCommon.i)) {
      pushOriginal('deleted', originalTokens[oIndex]);
      oIndex++;
    }

    while (cIndex < n && (!nextCommon || cIndex < nextCommon.j)) {
      pushCorrected('inserted', correctedTokens[cIndex]);
      cIndex++;
    }

    if (nextCommon && oIndex === nextCommon.i && cIndex === nextCommon.j) {
      pushOriginal('normal', originalTokens[oIndex]);
      pushCorrected('normal', correctedTokens[cIndex]);
      oIndex++;
      cIndex++;
      k++;
    }
  }

  return {
    originalSegments: originalSegments.length
      ? originalSegments
      : [{ text: originalText || '', type: 'normal' }],
    correctedSegments: correctedSegments.length
      ? correctedSegments
      : [{ text: correctedText || '', type: 'normal' }],
  };
}

function createEmptyTaskResult(
  originalText: string,
  wordCount: number,
  wordLimit: { min: number; max: number }
): TaskResult {
  return {
    originalText,
    correctedText: '',
    nclcLevel: '—',
    wordCount,
    wordLimit,
    taskFulfillment: '',
    grammarErrors: [],
    vocabularyUpgrades: [],
    connectorsUsed: [],
    connectorsMissed: [],
    originalSegments: [{ text: originalText || '', type: 'normal' }],
    correctedSegments: [{ text: '', type: 'normal' }],
    highScoringPatternsFound: [],
    suggestedPatterns: [],
  };
}

function aiResultToTaskResult(
  originalText: string,
  wordCount: number,
  wordLimit: { min: number; max: number },
  aiResult: AiResult
): TaskResult {
  const s = aiResult.scores;
  const f = aiResult.feedback;
  const nclcLevel = `NCLC ${s.nclc_level} / ${s.cefr_level}`;
  const correctedText = aiResult.corrected_text || originalText;
  const { originalSegments, correctedSegments } = computeDiffSegments(originalText, correctedText);
  const vocabularyUpgrades = (f.vocabulary_upgrades ?? []).map((v) => ({
    original: v.original_word,
    suggestion: v.better_alternative,
    reason: v.reason,
  }));
  const highScoringPatternsFound = (f.high_scoring_patterns_found ?? []).map((p) => ({
    excerpt: p.excerpt,
    patternName: p.pattern_name,
    explanation: p.explanation,
  }));
  const suggestedPatterns = (f.suggested_patterns ?? []).map((p) => ({
    patternName: p.pattern_name,
    originalSentence: p.original_sentence,
    improvedSentence: p.improved_sentence,
    explanation: p.explanation,
  }));
  return {
    originalText,
    correctedText,
    nclcLevel,
    wordCount,
    wordLimit,
    taskFulfillment: f.task_fulfillment ?? '',
    grammarErrors: f.grammar_errors ?? [],
    vocabularyUpgrades,
    connectorsUsed: f.connectors_used ?? [],
    connectorsMissed: f.missing_connectors ?? [],
    originalSegments,
    correctedSegments,
    highScoringPatternsFound,
    suggestedPatterns,
  };
}

@Component({
  selector: 'app-writing-results',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './writing-results.component.html',
  styleUrl: './writing-results.component.scss',
  imports: [AccordionModule, CardModule],
})
export class WritingResultsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly session = inject(WritingSessionService);
  private readonly writingHistory = inject(WritingHistoryService);

  protected readonly fromHistory = signal(false);
  protected readonly activeTaskIndex = signal(0);
  protected readonly combination = signal<TopicCombination | null>(null);
  protected readonly answers = signal<string[]>(['', '', '']);
  protected readonly record = signal<WritingExamRecord | null>(null);
  protected readonly loading = signal(false);

  protected readonly taskResults = computed(() => {
    const rec = this.record();
    const ans = this.answers();

    if (rec?.tasks) {
      return [1, 2, 3].map((i) => {
        const key = `task${i}`;
        const t = rec.tasks[key];
        const wordLimit = TASK_WORD_LIMITS[i - 1] ?? { min: 60, max: 120 };
        if (t?.aiResult) {
          return aiResultToTaskResult(
            t.originalText,
            t.wordCount,
            wordLimit,
            t.aiResult
          );
        }
        return createEmptyTaskResult(t?.originalText ?? '', t?.wordCount ?? 0, wordLimit);
      });
    }

    return ans.map((text, i) => {
      const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
      const wordLimit = TASK_WORD_LIMITS[i] ?? { min: 60, max: 120 };
      return createEmptyTaskResult(text, wordCount, wordLimit);
    });
  });

  protected readonly isTaskGraded = computed(() =>
    this.taskResults().map((r) => r.wordCount >= MIN_WORDS_FOR_GRADING)
  );

  protected readonly estimatedNclc = computed(() => {
    const rec = this.record();
    if (rec?.overallNclc != null) return `NCLC ${rec.overallNclc}`;
    const results = this.taskResults();
    const graded = results.filter((_, i) => this.isTaskGraded()[i]);
    if (graded.length === 0) return '—';
    return graded[0].nclcLevel;
  });

  protected readonly activeTaskResult = computed(() => {
    const idx = this.activeTaskIndex();
    return this.taskResults()[idx] ?? null;
  });

  protected readonly activePrompt = computed(() => {
    const c = this.combination();
    const idx = this.activeTaskIndex();
    if (!c) return '';
    const tasks = [c.tache1, c.tache2, c.tache3] as const;
    const t = tasks[idx];
    return t ? t.prompt : '';
  });

  protected readonly activeDocuments = computed(() => {
    const c = this.combination();
    if (!c) return [] as string[];
    const idx = this.activeTaskIndex();
    if (idx !== 2) return [] as string[];
    const t3 = c.tache3 as { document1?: string; document2?: string } | undefined;
    const docs: string[] = [];
    if (t3?.document1) docs.push(t3.document1);
    if (t3?.document2) docs.push(t3.document2);
    return docs;
  });

  protected readonly isActiveTaskGraded = computed(() => {
    const idx = this.activeTaskIndex();
    return this.isTaskGraded()[idx] ?? false;
  });

  ngOnInit(): void {
    const state = history.state as {
      combination?: TopicCombination;
      fromHistory?: boolean;
      answers?: string[];
      record?: WritingExamRecord;
      combinationId?: string;
    } | undefined;

    this.fromHistory.set(!!state?.fromHistory);

    if (state?.record) {
      this.record.set(state.record);
      const comboFromState = state.combination ?? null;
      const comboFromRecord = this.recordToCombination(state.record);
      this.combination.set(comboFromState ?? comboFromRecord);
      this.answers.set(this.recordToAnswers(state.record));
    } else if (state?.combinationId) {
      this.loading.set(true);
      this.writingHistory.getByCombinationId(state.combinationId).then(
        (rec) => {
          this.record.set(rec);
          this.combination.set(this.recordToCombination(rec));
          this.answers.set(this.recordToAnswers(rec));
        },
        () => this.router.navigate(['/writing'])
      ).finally(() => this.loading.set(false));
    } else if (state?.combination && Array.isArray(state?.answers)) {
      this.combination.set(state.combination);
      this.answers.set(state.answers);
    } else {
      this.router.navigate(['/writing']);
      return;
    }
  }

  private recordToCombination(rec: WritingExamRecord): TopicCombination | null {
    const [year, month, combinationNumber] = rec.combinationId.split('-').map(Number);
    if (!year || !month || !combinationNumber) return null;
    const t1 = rec.tasks?.['task1'];
    const t2 = rec.tasks?.['task2'];
    const t3 = rec.tasks?.['task3'];
    return {
      id: rec.combinationId,
      year,
      month,
      combinationNumber,
      label: rec.combinationId,
      tache1: { prompt: t1?.prompt ?? '' },
      tache2: { prompt: t2?.prompt ?? '' },
      tache3: {
        prompt: t3?.prompt ?? '',
        document1: undefined,
        document2: undefined,
      },
    };
  }

  private recordToAnswers(rec: WritingExamRecord): string[] {
    return [
      rec.tasks?.['task1']?.originalText ?? '',
      rec.tasks?.['task2']?.originalText ?? '',
      rec.tasks?.['task3']?.originalText ?? '',
    ];
  }

  protected setActiveTask(index: number): void {
    this.activeTaskIndex.set(index);
  }

  protected goToPractice(c: TopicCombination): void {
    const ans = this.answers();
    this.router.navigate(['/writing/practice', c.year, c.month, c.combinationNumber], {
      state: { selectedCombination: c, answers: ans.length ? [...ans] : undefined }
    });
  }

  protected goBack(): void {
    if (this.fromHistory()) {
      this.router.navigate(['/writing/history']);
      return;
    }

    const c = this.combination();
    const ans = this.answers();
    if (c && ans.length) {
      this.session.saveSession(c, [...ans]);
      this.router.navigate(['/writing/practice', c.year, c.month, c.combinationNumber], {
        state: { selectedCombination: c }
      });
    } else {
      this.router.navigate(['/writing']);
    }
  }

  protected getTaskBadgeClass(graded: boolean): string {
    return graded
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-rose-50 text-rose-700 border-rose-200';
  }
}
